import { useMemo } from 'react';

import type { DomainQueryOptions } from '@/domain/shared/types';
import { useBehaviorController_getSwimSpeed } from '@/services/hooks';
import type { SwimSpeedResponseDto } from '@/services/types';

import { mapSwimSpeedResponseToDomain } from './mappers';
import { toBehaviorRangeQuery } from './queryParams';
import type { BehaviorDateRangeParams, UseGetSwimSpeedResult } from './types';

const EMPTY_SWIM_SPEED_DATA: SwimSpeedResponseDto = {
    data: [],
    lastMeasurement: '',
};

/**
 * Domain hook to fetch swim speed data for a cage
 * Returns swim speed data points including temperature and depth
 * for the specified date range
 *
 * @param cageId - The cage ID to fetch swim speed data for
 * @param params - Optional date range parameters (from/to in YYYY-MM-DD format)
 * @param options - Optional React Query options (e.g. enabled, refetchInterval)
 * @returns Query result with mapped swim speed chart data
 */
export const useGetSwimSpeed = (
    cageId: number,
    params?: BehaviorDateRangeParams,
    options?: DomainQueryOptions<SwimSpeedResponseDto>
): UseGetSwimSpeedResult => {
    const swimSpeedQuery = useBehaviorController_getSwimSpeed(
        cageId,
        toBehaviorRangeQuery(params),
        options
    );

    const swimSpeedData = useMemo(
        () => mapSwimSpeedResponseToDomain(swimSpeedQuery.data?.data ?? EMPTY_SWIM_SPEED_DATA),
        [swimSpeedQuery.data?.data]
    );

    return {
        ...swimSpeedQuery,
        isLoading: swimSpeedQuery.isPending,
        data: swimSpeedData,
    };
};
